import {
  Component,
  type ComponentProps,
  type ComponentType,
  type ReactNode,
} from "react";
import { useLocation } from "@remix-run/react";
import * as Sentry from "@sentry/remix";

type Props = { name: string; children: ReactNode; fallback?: ReactNode };

// Keeps a client-side render crash inside one section, so it can't reach the route
// boundary and de-index the page (WOV-2200). Server renders are unaffected: React's
// streaming renderer ignores error boundaries, so an SSR throw still 500s as before.
export default class WvSafeRender extends Component<Props, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    Sentry.captureException(error, { tags: { safeRender: this.props.name } });
  }

  render() {
    return this.state.failed
      ? (this.props.fallback ?? null)
      : this.props.children;
  }
}

// Wraps a non-critical component so its crash removes only that section. Keyed on the
// path so a failure on one page doesn't leave the section blank for the whole session.
export const safe = <C extends ComponentType<never>>(name: string, Wrapped: C) => {
  const Safe = (props: ComponentProps<C>) => {
    const { pathname } = useLocation();
    return (
      <WvSafeRender key={pathname} name={name}>
        <Wrapped {...props} />
      </WvSafeRender>
    );
  };
  Safe.displayName = `Safe(${name})`;
  return Safe;
};

// Contains each slice separately, so one bad slice can't blank a whole content page.
export const withSafeRender = <T extends Record<string, ComponentType<never>>>(
  components: T
): T =>
  Object.fromEntries(
    Object.entries(components).map(([name, Slice]) => [
      name,
      safe(`slice:${name}`, Slice),
    ])
  ) as T;
